import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthToken } from '../utils/api';

// ─── Authenticated axios helper — kiosk endpoints all require login ─────────
const authHeaders = () => {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const authGet = (url, config = {}) =>
  axios.get(url, { withCredentials: true, ...config, headers: { ...authHeaders(), ...(config.headers || {}) } });
const authPost = (url, body, config = {}) =>
  axios.post(url, body, { withCredentials: true, ...config, headers: { ...authHeaders(), ...(config.headers || {}) } });

// ─────────────────────────────────────────────────────────────────────────────
// MusicSync Kiosk Player  —  hardened rewrite
//
// Why this file is deliberately boring:
//   • It uses the native <audio> element, no Web Audio graph. Previous versions
//     ran <audio> through AudioContext.createMediaElementSource() on mount,
//     which captures the element and gates its pipeline on the context state.
//     If the context was suspended (the default before user gesture), the
//     element reported paused=false and the 'play' event fired, but currentTime
//     never advanced — progress frozen, no sound, no recovery.
//   • All "am I playing" state is driven by the DOM 'playing' / 'pause' /
//     'ended' events. The 'play' event (which can fire even when autoplay is
//     blocked) is ignored.
//   • A single tap-to-start overlay is shown on every fresh kiosk load, not
//     lazily after autoplay fails. This guarantees we own the first gesture
//     and satisfies every browser's autoplay policy deterministically.
// ─────────────────────────────────────────────────────────────────────────────

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILE_BASE = process.env.REACT_APP_BACKEND_URL;
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');

const DB_NAME = 'musicsync_kiosk';
const DB_VERSION = 1;
const SCHEDULE_POLL_MS = 60_000;
const HEARTBEAT_MS = 5_000;
const MAX_CONSECUTIVE_TRACK_ERRORS = 3;
// Tiny silent WAV used to "prime" the second <audio> element on the first
// user gesture — Safari/iOS requires each element to be unlocked by a
// gesture before programmatic play() is allowed (needed for crossfade).
const SILENT_WAV = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==';

// ─── IndexedDB helpers (offline cache) ───────────────────────────────────────
function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains('schedules')) d.createObjectStore('schedules');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbPut(key, value) {
  const d = await openDB();
  return new Promise((res, rej) => {
    const tx = d.transaction('schedules', 'readwrite');
    tx.objectStore('schedules').put(value, key);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}
async function idbGet(key) {
  const d = await openDB();
  return new Promise((res, rej) => {
    const tx = d.transaction('schedules', 'readonly');
    const r = tx.objectStore('schedules').get(key);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

const formatTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

const Kiosk = () => {
  const { storeId } = useParams();

  // ── State ────────────────────────────────────────────────────────────────
  const [tracks, setTracks] = useState([]);
  const [playlistName, setPlaylistName] = useState('');
  const [scheduleType, setScheduleType] = useState('none');
  // Playlist-level transition settings pushed from the backend. When
  // transition==='fade' the kiosk uses a second <audio> element to overlap
  // the tail of the current track with the head of the next one, ramping
  // volumes in opposite directions across `fadeDurationMs` milliseconds.
  const [transitionMode, setTransitionMode] = useState('cut');
  const [fadeDurationMs, setFadeDurationMs] = useState(2500);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineMode, setOfflineMode] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [needsUserGesture, setNeedsUserGesture] = useState(true); // shown until first tap
  const [fatalPlayError, setFatalPlayError] = useState(null);
  const [branding, setBranding] = useState({
    accent_color: '#3b82f6',
    background_value: 'radial-gradient(ellipse at center, #1a1a2e 0%, #09090b 70%)',
    text_color: '#ffffff',
    secondary_text: '#a1a1aa',
    show_track_info: true,
    show_visualizer: true,
  });
  const [sessionReplaced, setSessionReplaced] = useState(false);
  // Captured at mount from navigator. Geolocation is asynchronous and
  // gated on a user prompt, so it lands later via handleUnlock.
  const [deviceInfo, setDeviceInfo] = useState({
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    geolocation: null,
  });
  const deviceInfoRef = useRef(deviceInfo);
  useEffect(() => { deviceInfoRef.current = deviceInfo; }, [deviceInfo]);

  // ── Refs ─────────────────────────────────────────────────────────────────
  const audioRef = useRef(null);
  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const trackErrorCountRef = useRef(0);      // consecutive errors on CURRENT track
  const consecutiveBadTracksRef = useRef(0); // consecutive tracks that failed entirely
  const userUnlockedRef = useRef(false);     // first-tap-done

  // Keep the latest playable state accessible inside event handlers without
  // re-binding them on every render.
  const tracksRef = useRef(tracks);
  const trackIdxRef = useRef(currentTrackIndex);
  const volumeRef = useRef(volume);
  // Sticky flag: was the current pause caused by a user/operator command?
  // If yes, the auto-start useEffect must NOT auto-resume (that was the
  // v23 regression where hitting Pause on the dashboard just kept resuming
  // itself). Cleared any time a new play() is intentionally issued.
  const userPausedRef = useRef(false);
  // Fade refs — kept out of React state because they change 30x/sec during a
  // fade and would thrash re-renders otherwise.
  const nextAudioRef = useRef(null);
  const fadeInProgressRef = useRef(false);
  const fadeIntervalRef = useRef(null);
  // True when the ACTIVE element fired 'ended' while a fade was in progress
  // (the event is swallowed so it can't double-advance). If the fade then
  // ABORTS, we must advance manually or the player replays one song forever.
  const endedDuringFadeRef = useRef(false);
  const fadeStartedAtRef = useRef(0);
  const transitionModeRef = useRef('cut');
  const fadeDurationMsRef = useRef(2500);
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);
  useEffect(() => { trackIdxRef.current = currentTrackIndex; }, [currentTrackIndex]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);
  useEffect(() => { transitionModeRef.current = transitionMode; }, [transitionMode]);
  useEffect(() => { fadeDurationMsRef.current = fadeDurationMs; }, [fadeDurationMs]);

  // ── Create the <audio> elements once — plain HTMLAudioElements, no Web Audio ─
  // Two elements exist so we can crossfade: while `audioRef.current` fades
  // out playing the tail of the current track, `nextAudioRef.current` fades
  // in playing the head of the next track. When the fade completes we swap
  // pointers so the "next" becomes the new "current".
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'auto';
    // NOTE: we deliberately do NOT set crossOrigin = 'anonymous'. Setting it
    // puts the browser into strict-CORS mode for media loads, and the moment
    // any deployed proxy/ingress strips or alters Access-Control-Allow-Origin
    // on a streaming response, the browser SILENTLY refuses to play the audio
    // (no 'error' event, no console message — the element just never produces
    // sound). Since we removed Web Audio API usage, we don't need CORS on the
    // media element at all. Leaving crossOrigin unset means the browser will
    // happily play any audio URL, cross-origin or not, regardless of what
    // CORS headers the backend returns. This is the single most reliable
    // fix for "works on preview, fails on production" audio bugs.
    audio.volume = volumeRef.current;
    audioRef.current = audio;

    const nextAudio = new Audio();
    nextAudio.preload = 'auto';
    nextAudio.volume = 0;
    nextAudioRef.current = nextAudio;

    return () => {
      try { audio.pause(); audio.src = ''; audio.load(); } catch (e) { /* ignore */ }
      try { nextAudio.pause(); nextAudio.src = ''; nextAudio.load(); } catch (e) { /* ignore */ }
      if (fadeIntervalRef.current) { clearInterval(fadeIntervalRef.current); fadeIntervalRef.current = null; }
    };
  }, []);

  // ── Network state tracking ───────────────────────────────────────────────
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // ── Volume control (direct to element) ───────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const v = Math.max(0, Math.min(1, volume));
    // Apply in both places — browsers treat `muted` as a separate gate and
    // volume=0 doesn't always un-mute once muted, so we reset muted whenever
    // a non-zero volume is requested.
    // Only touch the ACTIVE element. If a fade is in progress, the fade
    // orchestrator owns the volume of both elements until it finishes.
    if (!fadeInProgressRef.current) {
      audio.volume = v;
      if (v > 0 && audio.muted) audio.muted = false;
    }
  }, [volume]);

  // ── Access check + branding ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true, headers });
        if (res.data.role === 'kiosk' && res.data.store_id && res.data.store_id !== storeId) {
          setAccessDenied(true);
        }
      } catch (e) { /* /auth/me can 401 for public kiosk URLs; that's fine */ }
    })();
    (async () => {
      try {
        const res = await authGet(`${API}/kiosk/${storeId}/branding`);
        if (res.data) setBranding((prev) => ({ ...prev, ...res.data }));
      } catch (e) { /* branding is optional */ }
    })();
  }, [storeId]);

  // ── Status reporter (fire-and-forget) ────────────────────────────────────
  const sendStatus = useCallback((online, playing, track, idx) => {
    const audio = audioRef.current;
    authPost(`${API}/kiosk/${storeId}/update-status`, {
      is_online: online,
      is_playing: playing,
      current_track: track ? { title: track.title, artist: track.artist } : null,
      current_time: audio?.currentTime || 0,
      duration: audio?.duration || 0,
      current_track_index: idx ?? trackIdxRef.current,
      user_agent: deviceInfoRef.current.user_agent,
      geolocation: deviceInfoRef.current.geolocation,
    }).catch(() => {});
  }, [storeId]);

  // ── Core play() wrapper ──────────────────────────────────────────────────
  // Plays the track at `idx`. Returns a promise that resolves when the first
  // 'playing' event fires, or rejects if the browser blocks playback or the
  // source errors out. On autoplay block we surface the tap-to-start overlay.

  // Build the fully-qualified media URL for a track, appending the JWT as a
  // `?auth=<token>` query-string param (HTML <audio> can't send auth
  // headers). Shared by playTrackAt AND the crossfade preloader so both
  // elements point at the same authenticated endpoints.
  const buildTrackSrc = useCallback((track) => {
    if (!track?.file_url) return null;
    const token = getAuthToken();
    const separator = track.file_url.includes('?') ? '&' : '?';
    return token
      ? `${FILE_BASE}${track.file_url}${separator}auth=${encodeURIComponent(token)}`
      : `${FILE_BASE}${track.file_url}`;
  }, []);

  const playTrackAt = useCallback((idx) => {
    const list = tracksRef.current;
    if (!list.length || idx < 0 || idx >= list.length) return;
    const track = list[idx];
    const src = buildTrackSrc(track);
    if (!src) return;
    const audio = audioRef.current;
    if (!audio) return;

    // If a crossfade is in flight, abort it — the caller wants a hard cut
    // to a specific track (e.g. remote change_track command).
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
    fadeInProgressRef.current = false;
    // A new track load is by definition NOT an operator pause. Clear the
    // sticky flag so the auto-start effect doesn't accidentally suppress
    // the very play() call we're about to issue.
    userPausedRef.current = false;
    // Reset both elements so a stale nextAudio doesn't keep playing.
    try {
      const na = nextAudioRef.current;
      if (na) { na.pause(); na.currentTime = 0; na.volume = 0; }
    } catch (e) { /* ignore */ }
    audio.volume = volumeRef.current;

    trackErrorCountRef.current = 0;
    setFatalPlayError(null);

    if (audio.src !== src) {
      audio.src = src;
      try { audio.load(); } catch (e) { /* ignore */ }
    }

    const kick = () => {
      audio.play().then(() => {
        setCurrentTrackIndex(idx);
        setNeedsUserGesture(false);
      }).catch((err) => {
        console.warn('[kiosk] play() rejected:', err?.name, err?.message);
        setCurrentTrackIndex(idx);
        if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
          // Autoplay / user-gesture policy — show overlay, wait for tap
          setNeedsUserGesture(true);
        }
      });
    };

    // Browsers need a small moment to prime the pipeline after .load().
    // Using 'canplay' is tempting but some browsers (and Drive streams with
    // chunked transfer encoding) don't fire it reliably. A short timeout is
    // the more robust approach.
    if (audio.readyState >= 2 /* HAVE_CURRENT_DATA */ && audio.src === src) {
      kick();
    } else {
      let fired = false;
      const onReady = () => {
        if (fired) return; fired = true;
        audio.removeEventListener('loadedmetadata', onReady);
        audio.removeEventListener('canplay', onReady);
        clearTimeout(timeout);
        kick();
      };
      const timeout = setTimeout(onReady, 1500);
      audio.addEventListener('loadedmetadata', onReady, { once: true });
      audio.addEventListener('canplay', onReady, { once: true });
    }
  }, [buildTrackSrc]);

  // ── Crossfade orchestrator ──────────────────────────────────────────────
  // When the current track is within `fadeDurationMs` of its end AND the
  // playlist's transition mode is 'fade', preload the next track into
  // nextAudioRef, start it playing at volume 0, then linearly ramp the
  // volumes across `fadeDurationMs` — current → 0, next → user's volume.
  // At the end we swap the refs so the "next" becomes the new "current"
  // and the old audio element is free to be reused for the following fade.
  const startCrossfadeToNext = useCallback(() => {
    if (fadeInProgressRef.current) return;
    const list = tracksRef.current;
    if (list.length === 0) return;
    const nextIdx = (trackIdxRef.current + 1) % list.length;
    const nextTrack = list[nextIdx];
    const nextSrc = buildTrackSrc(nextTrack);
    if (!nextSrc) return;
    const current = audioRef.current;
    const next = nextAudioRef.current;
    if (!current || !next) return;
    const userVol = volumeRef.current;
    if (userVol <= 0) return; // nothing to fade

    fadeInProgressRef.current = true;
    fadeStartedAtRef.current = Date.now();
    endedDuringFadeRef.current = false;
    next.src = nextSrc;
    next.volume = 0;
    try { next.load(); } catch (e) { /* ignore */ }

    // Hard-cut recovery for a fade that cannot proceed (next.play() rejected
    // or hung). CRITICAL: if the current track already finished while the
    // fade was pending, its 'ended' event was swallowed by the
    // fadeInProgress guard — so we MUST advance here ourselves. Relying on
    // "ended will fall through" replays the same song forever.
    let aborted = false;
    const abortFadeAndRecover = () => {
      if (aborted) return;
      aborted = true;
      clearTimeout(playTimeout);
      const wasFading = fadeInProgressRef.current;
      fadeInProgressRef.current = false;
      try { next.pause(); next.removeAttribute('src'); next.load(); } catch (e) { /* ignore */ }
      if (!wasFading) return; // a hard cut (playTrackAt) already took over
      const cur = audioRef.current;
      const atEof = cur && cur.duration && Number.isFinite(cur.duration)
        && cur.currentTime > 0 && (cur.duration - cur.currentTime) <= 0.5;
      if (endedDuringFadeRef.current || (cur && (cur.ended || (cur.paused && atEof)))) {
        endedDuringFadeRef.current = false;
        console.warn('[kiosk] fade aborted after track end — hard-cutting to next');
        const l = tracksRef.current;
        if (l.length) playTrackAt((trackIdxRef.current + 1) % l.length);
      }
    };
    // Watchdog: if next.play() neither resolves nor rejects (hung Drive
    // stream), fadeInProgress would stay true forever and mute every
    // auto-advance guard in the player. Abort after 8s.
    const playTimeout = setTimeout(abortFadeAndRecover, 8000);

    next.play().then(() => {
      clearTimeout(playTimeout);
      if (aborted || !fadeInProgressRef.current) return;
      const durationMs = Math.max(500, Math.min(8000, fadeDurationMsRef.current));
      const steps = 30;
      const stepMs = Math.max(30, Math.floor(durationMs / steps));
      let elapsed = 0;
      fadeIntervalRef.current = setInterval(() => {
        elapsed += stepMs;
        const t = Math.min(1, elapsed / durationMs);
        try {
          current.volume = Math.max(0, userVol * (1 - t));
          next.volume = Math.min(1, userVol * t);
        } catch (e) { /* ignore */ }
        if (t >= 1) {
          clearInterval(fadeIntervalRef.current);
          fadeIntervalRef.current = null;
          // Swap: `next` is now the playing element; the OLD `current`
          // becomes the free slot for the next fade.
          try { current.pause(); current.currentTime = 0; current.src = ''; } catch (e) { /* ignore */ }
          audioRef.current = next;
          nextAudioRef.current = current;
          fadeInProgressRef.current = false;
          endedDuringFadeRef.current = false;
          // The 'playing' event fired on `next` BEFORE the swap (when it was
          // still inactive) so it was ignored — reflect the real state here.
          setIsPlaying(!next.paused);
          setCurrentTrackIndex(nextIdx);
        }
      }, stepMs);
    }).catch(() => {
      abortFadeAndRecover();
    });
  }, [buildTrackSrc, playTrackAt]);

  // Trigger fade when the current track's remaining time enters the fade
  // window. We use a 250ms interval rather than a `timeupdate` listener
  // because `audioRef.current` gets swapped mid-fade, and an old event
  // listener would still be bound to the previous element.
  useEffect(() => {
    const check = () => {
      if (transitionModeRef.current !== 'fade') return;
      if (fadeInProgressRef.current) return;
      const audio = audioRef.current;
      if (!audio || audio.paused) return;
      if (!audio.duration || !Number.isFinite(audio.duration)) return;
      // Small guard-band so we don't try to fade a 2-second-long track.
      if (audio.duration * 1000 < fadeDurationMsRef.current * 2) return;
      const remainingMs = (audio.duration - audio.currentTime) * 1000;
      if (remainingMs <= fadeDurationMsRef.current) startCrossfadeToNext();
    };
    const iv = setInterval(check, 250);
    return () => clearInterval(iv);
  }, [startCrossfadeToNext]);

  // ── Advance next / prev ──────────────────────────────────────────────────
  const playNext = useCallback(() => {
    const t = tracksRef.current;
    if (!t.length) return;
    const nextIdx = (trackIdxRef.current + 1) % t.length;
    playTrackAt(nextIdx);
  }, [playTrackAt]);
  const playPrev = useCallback(() => {
    const t = tracksRef.current;
    if (!t.length) return;
    const prevIdx = (trackIdxRef.current - 1 + t.length) % t.length;
    playTrackAt(prevIdx);
  }, [playTrackAt]);

  // ── <audio> DOM event wiring ─────────────────────────────────────────────
  // Listeners are attached to BOTH audio elements (primary + fade buffer)
  // so that a mid-fade ref swap doesn't leave the auto-advance chain
  // orphaned on the OLD element. Each handler starts with a guard
  // `if (this !== audioRef.current) return;` so only the currently-active
  // element drives global state (currentTime, isPlaying, ended → playNext).
  useEffect(() => {
    const a1 = audioRef.current;
    const a2 = nextAudioRef.current;
    if (!a1 || !a2) return;

    const isActive = (target) => target === audioRef.current;

    const onTimeUpdate = (e) => {
      if (!isActive(e.currentTarget)) return;
      const audio = e.currentTarget;
      setCurrentTime(audio.currentTime || 0);
      setDuration(audio.duration || 0);
      if (audio.duration > 0) setProgress((audio.currentTime / audio.duration) * 100);
    };
    const onPlaying = (e) => {
      if (!isActive(e.currentTarget)) return;
      setIsPlaying(true);
      setNeedsUserGesture(false);
      consecutiveBadTracksRef.current = 0;
      const track = tracksRef.current[trackIdxRef.current];
      sendStatus(true, true, track);
    };
    const onPause = (e) => {
      if (!isActive(e.currentTarget)) return;
      const audio = e.currentTarget;
      setIsPlaying(false);
      // EOF-without-ended fallback: some Drive streams fire `pause` at end
      // of file but never `ended`. Detect it here and advance immediately
      // (instead of waiting up to 2s for the watchdog).
      const atEof = audio.ended || (
        audio.duration && Number.isFinite(audio.duration)
        && audio.currentTime > 0
        && (audio.duration - audio.currentTime) <= 0.5
      );
      if (atEof && !fadeInProgressRef.current) {
        playNext();
        return;
      }
      const track = tracksRef.current[trackIdxRef.current];
      sendStatus(true, false, track);
    };
    const onEnded = (e) => {
      // If a crossfade is running, the fade orchestrator owns the advance.
      // Swallow the ACTIVE element's ended event but RECORD it — if the fade
      // later aborts, abortFadeAndRecover uses this flag to hard-advance
      // (otherwise the swallowed event means nothing ever moves on).
      if (fadeInProgressRef.current) {
        if (isActive(e.currentTarget)) endedDuringFadeRef.current = true;
        return;
      }
      if (!isActive(e.currentTarget)) return;
      consecutiveBadTracksRef.current = 0;
      playNext();
    };
    const reportSkip = (reason, code) => {
      const track = tracksRef.current[trackIdxRef.current];
      authPost(`${API}/kiosk/${storeId}/report-playback-error`, {
        track_id: track?.id || null,
        title: track?.title || 'unknown',
        reason,
        media_error_code: code ?? null,
      }).catch(() => {});
    };
    const onError = (e) => {
      if (!isActive(e.currentTarget)) return;
      const audio = e.currentTarget;
      const code = audio?.error?.code || null;
      console.warn('[kiosk] track error', trackErrorCountRef.current + 1, audio?.error);
      // MEDIA_ERR_SRC_NOT_SUPPORTED (4) = the stream endpoint answered with
      // an HTTP error (Drive 403/404: revoked grant, download-disabled shared
      // file, moved/deleted file) or the format is unplayable. Retrying is
      // pointless — report the reason and skip fast (~1s gap) so music keeps
      // flowing.
      if (code === 4) {
        trackErrorCountRef.current = 0;
        consecutiveBadTracksRef.current += 1;
        reportSkip('source_unplayable', code);
        if (consecutiveBadTracksRef.current >= MAX_CONSECUTIVE_TRACK_ERRORS) {
          consecutiveBadTracksRef.current = 0;
          setFatalPlayError('Multiple tracks failed to play. Will retry schedule shortly.');
          setIsPlaying(false);
          setTimeout(() => { scheduleFetchRef.current?.(); }, 15000);
          return;
        }
        setTimeout(playNext, 1000);
        return;
      }
      trackErrorCountRef.current += 1;
      if (trackErrorCountRef.current <= 1) {
        setTimeout(() => {
          try { audio.load(); audio.play().catch(() => {}); } catch (er) { /* ignore */ }
        }, 2000);
      } else {
        trackErrorCountRef.current = 0;
        consecutiveBadTracksRef.current += 1;
        reportSkip('media_error_retry_failed', code);
        if (consecutiveBadTracksRef.current >= MAX_CONSECUTIVE_TRACK_ERRORS) {
          consecutiveBadTracksRef.current = 0;
          setFatalPlayError('Multiple tracks failed to play. Will retry schedule shortly.');
          setIsPlaying(false);
          setTimeout(() => { scheduleFetchRef.current?.(); }, 15000);
          return;
        }
        setTimeout(playNext, 3000);
      }
    };

    const bind = (el) => {
      el.addEventListener('timeupdate', onTimeUpdate);
      el.addEventListener('playing', onPlaying);
      el.addEventListener('pause', onPause);
      el.addEventListener('ended', onEnded);
      el.addEventListener('error', onError);
      // NOTE: 'stalled' is deliberately NOT treated as an error. It fires
      // routinely on Drive streams while playback continues fine from the
      // buffer; treating it as an error used to load()+play() → the song
      // restarted from 0:00 mid-play. Genuine stalls (frozen currentTime)
      // are caught by the 20s stall watchdog below.
    };
    const unbind = (el) => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
    };
    bind(a1);
    bind(a2);
    return () => { unbind(a1); unbind(a2); };
  }, [playNext, sendStatus, storeId]);

  // ── Stall watchdog ───────────────────────────────────────────────────────
  // ── Stall watchdog + end-of-track fallback ─────────────────────────────
  // Two problems this catches, both silent on their own:
  //   1. Audio claims to be playing but currentTime is frozen (silent
  //      failure — CORS blocked stream, network hang) → treat as failure
  //      and skip.
  //   2. Drive streams occasionally reach the end of the file and go
  //      paused WITHOUT firing the `ended` event, so the auto-advance
  //      chain never triggers and the playlist appears "stuck on the
  //      last track" or "stops looping". If the paused audio is within
  //      ~0.5s of duration, treat it as a completed track and playNext.
  useEffect(() => {
    let lastTime = -1;
    let stuckCount = 0;
    // 2s tick — fast enough to catch a silent-end-of-track within ~2s
    // (below human "did the music just stop?" threshold), slow enough that
    // the stuck-check still requires ~20s of no progress before it treats
    // playback as failed (stuckCount >= 10 * 2s tick = 20s).
    const iv = setInterval(() => {
      const audio = audioRef.current;
      if (!audio) return;
      // End-of-track fallback — fires ONCE per stuck-at-end situation.
      // Guarded on fadeInProgressRef so we don't double-advance during a
      // crossfade (the fade orchestrator handles the swap itself).
      if (fadeInProgressRef.current) {
        // Failsafe: a crossfade must never outlive its duration by >15s.
        // A hung fade would otherwise keep fadeInProgress=true forever and
        // mute every auto-advance guard (player stuck on one song).
        if (Date.now() - fadeStartedAtRef.current > fadeDurationMsRef.current + 15000) {
          console.warn('[kiosk] fade stuck — force-clearing and advancing');
          if (fadeIntervalRef.current) { clearInterval(fadeIntervalRef.current); fadeIntervalRef.current = null; }
          fadeInProgressRef.current = false;
          endedDuringFadeRef.current = false;
          playNext();
        }
        lastTime = -1; stuckCount = 0;
        return;
      }
      if (audio.paused && (audio.ended || audio.duration)) {
        const remaining = (audio.duration || 0) - (audio.currentTime || 0);
        if (audio.ended || (Number.isFinite(remaining) && remaining <= 0.5 && audio.currentTime > 0)) {
          console.warn('[kiosk] end-of-track without ended event — advancing');
          playNext();
          lastTime = -1; stuckCount = 0;
          return;
        }
      }
      if (audio.paused) { lastTime = -1; stuckCount = 0; return; }
      const now = audio.currentTime || 0;
      if (lastTime >= 0 && Math.abs(now - lastTime) < 0.1) {
        stuckCount += 1;
        if (stuckCount >= 10) {
          // 20s (10 ticks × 2s) of zero progress while playing → failure.
          console.warn('[kiosk] stall watchdog tripped — advancing', { now, lastTime });
          stuckCount = 0;
          setFatalPlayError('Playback stalled. Skipping to next track.');
          const track = tracksRef.current[trackIdxRef.current];
          authPost(`${API}/kiosk/${storeId}/report-playback-error`, {
            track_id: track?.id || null,
            title: track?.title || 'unknown',
            reason: 'stalled_20s',
            media_error_code: null,
          }).catch(() => {});
          try { audio.pause(); } catch (e) { /* ignore */ }
          setTimeout(() => { setFatalPlayError(null); playNext(); }, 1500);
        }
      } else {
        stuckCount = 0;
      }
      lastTime = now;
    }, 2000);
    return () => clearInterval(iv);
  }, [playNext, storeId]);

  // ── Schedule fetch (debounced via ref) ──────────────────────────────────
  const scheduleFetchRef = useRef(null);
  const fetchSchedule = useCallback(async () => {
    const cacheKey = `schedule_${storeId}`;
    try {
      const res = await authGet(`${API}/kiosk/${storeId}/schedule`);
      const data = res.data;
      setError(null);
      setOfflineMode(false);
      if (typeof data.volume === 'number') setVolume(data.volume);
      setScheduleType(data.type || 'none');
      setPlaylistName(data.playlist?.name || '');
      // Playlist-level transition settings — default to 'cut' when the
      // playlist doesn't specify one (backwards-compat with pre-fade rows).
      setTransitionMode(data.playlist?.transition === 'fade' ? 'fade' : 'cut');
      const rawFade = parseInt(data.playlist?.fade_duration_ms, 10);
      setFadeDurationMs(Number.isFinite(rawFade) && rawFade >= 500 && rawFade <= 8000 ? rawFade : 2500);

      const newTracks = Array.isArray(data.tracks) ? data.tracks : [];
      const oldTracks = tracksRef.current;
      const newIds = newTracks.map((t) => t.id);
      const oldIds = oldTracks.map((t) => t.id);

      // Preserve the currently-playing track by ID, so re-ordering / adding
      // / removing other tracks from the playlist does not restart the song
      // the kiosk is currently playing.
      const currentId = oldIds[trackIdxRef.current];
      const newIdxOfCurrent = currentId ? newIds.indexOf(currentId) : -1;

      const setsEqual = newIds.length === oldIds.length
        && newIds.every((id) => oldIds.includes(id));

      if (!setsEqual) {
        // Content changed
        setTracks(newTracks);
        if (newTracks.length === 0) {
          // Schedule evaporated — stop playback
          try { audioRef.current?.pause(); } catch (e) { /* ignore */ }
        } else if (newIdxOfCurrent >= 0) {
          // Current song survived — just fix the index silently
          setCurrentTrackIndex(newIdxOfCurrent);
        } else if (!audioRef.current || audioRef.current.paused) {
          // Not currently playing → start from top once user has unlocked
          if (userUnlockedRef.current) {
            setTimeout(() => playTrackAt(0), 150);
          }
        } else {
          // Was playing a removed track → move to first track of new set
          setTimeout(() => playTrackAt(0), 150);
        }
      } else if (newIds.join(',') !== oldIds.join(',')) {
        // Same set, different order — keep the same song playing, fix idx.
        setTracks(newTracks);
        if (newIdxOfCurrent >= 0) setCurrentTrackIndex(newIdxOfCurrent);
      }
      await idbPut(cacheKey, data);
    } catch (err) {
      if (tracksRef.current.length > 0) return; // keep whatever's playing
      try {
        const cached = await idbGet(cacheKey);
        if (cached?.tracks?.length > 0) {
          setTracks(cached.tracks);
          setPlaylistName(cached.playlist?.name || '');
          setTransitionMode(cached.playlist?.transition === 'fade' ? 'fade' : 'cut');
          const rawFadeC = parseInt(cached.playlist?.fade_duration_ms, 10);
          setFadeDurationMs(Number.isFinite(rawFadeC) && rawFadeC >= 500 && rawFadeC <= 8000 ? rawFadeC : 2500);
          setScheduleType(cached.type || 'none');
          setOfflineMode(true);
          setError(null);
        } else {
          setError('No schedule available');
        }
      } catch (dbErr) {
        setError('No schedule available (offline)');
      }
    }
  }, [storeId, playTrackAt]);

  useEffect(() => { scheduleFetchRef.current = fetchSchedule; }, [fetchSchedule]);

  useEffect(() => {
    fetchSchedule();
    const iv = setInterval(fetchSchedule, SCHEDULE_POLL_MS);
    return () => clearInterval(iv);
  }, [fetchSchedule]);

  // ── Auto-start first track after user has unlocked audio ────────────────
  useEffect(() => {
    if (!userUnlockedRef.current) return;
    if (tracks.length === 0) return;
    const audio = audioRef.current;
    if (!audio) return;
    // Only auto-start when silent (first load, or after stop). Never disrupt
    // a song that's already playing.
    if (audio.paused && !audio.src) {
      playTrackAt(0);
    } else if (audio.paused && !isPlaying) {
      // If the operator/user intentionally paused, do NOT auto-resume.
      // Auto-resume is only for silent-start situations, not for
      // reversing an explicit pause command.
      if (userPausedRef.current) return;
      // Never touch playback mid-crossfade — the fade orchestrator owns
      // both elements. Calling play() here on the outgoing (ended) element
      // restarts it from 0:00 and loops the same song.
      if (fadeInProgressRef.current) return;
      // GUARD: if the current audio finished (paused at EOF, or `ended` was
      // missed — Drive streams do this), do NOT resume this track — that
      // would replay the same song forever. Advance instead (wraps to
      // index 0 via modulo, giving us the desired non-stop loop).
      if (audio.ended) { playNext(); return; }
      if (audio.duration && Number.isFinite(audio.duration)) {
        const remaining = audio.duration - audio.currentTime;
        if (remaining <= 0.5 && audio.currentTime > 0) {
          playNext();
          return;
        }
      }
      audio.play().catch(() => {});
    }
  }, [tracks, playTrackAt, playNext, isPlaying]);

  // ── Kiosk activity analytics ─────────────────────────────────────────────
  // page_load (nav_type 'reload' = refresh), page_close (keepalive fetch so
  // it survives unload), pause (remote stop). Playtime accrues via 30s
  // heartbeats while audio is actually playing.
  const reportKioskEvent = useCallback((type, extra = {}) => {
    try {
      const t = getAuthToken();
      fetch(`${API}/kiosk/${storeId}/analytics/event`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify({ type, ...extra }),
      }).catch(() => {});
    } catch (e) { /* ignore */ }
  }, [storeId]);

  useEffect(() => {
    if (!storeId) return;
    const navEntry = performance.getEntriesByType?.('navigation')?.[0];
    reportKioskEvent('page_load', { nav_type: navEntry?.type || 'navigate' });
    const onPageHide = () => reportKioskEvent('page_close');
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [storeId, reportKioskEvent]);

  useEffect(() => {
    if (!storeId) return;
    const iv = setInterval(() => {
      const a = audioRef.current;
      if (a && !a.paused && a.currentTime > 0) {
        authPost(`${API}/kiosk/${storeId}/analytics/heartbeat`, { seconds: 30 }).catch(() => {});
      }
    }, 30_000);
    return () => clearInterval(iv);
  }, [storeId]);

  // ── Remote command dispatcher (shared by WS and polling fallback) ───────
  // Both the WebSocket (instant) and the HTTP polling fallback (every 3s)
  // deliver the same commands with a unique `id`. We dedupe here so a
  // single dashboard click never produces a double-skip or double-play.
  const appliedCommandIdsRef = useRef(new Set());
  const applyRemoteCommand = useCallback((data) => {
    if (!data || data.type !== 'remote_command' || data.store_id !== storeId) return;
    const cid = data.id;
    if (cid) {
      if (appliedCommandIdsRef.current.has(cid)) return;
      appliedCommandIdsRef.current.add(cid);
      // Bound the set so it doesn't grow forever over a multi-day session.
      if (appliedCommandIdsRef.current.size > 200) {
        const arr = Array.from(appliedCommandIdsRef.current);
        appliedCommandIdsRef.current = new Set(arr.slice(-100));
      }
    }
    const audio = audioRef.current;
    if (data.action === 'play' && audio) { userPausedRef.current = false; audio.play().catch(() => {}); }
    else if (data.action === 'pause' && audio) { userPausedRef.current = true; audio.pause(); reportKioskEvent('pause'); }
    else if (data.action === 'skip') { userPausedRef.current = false; playNext(); }
    else if (data.action === 'prev') { userPausedRef.current = false; playPrev(); }
    else if (data.action === 'set_volume' && typeof data.volume === 'number') {
      const v = Math.max(0, Math.min(1, data.volume));
      setVolume(v);
      if (audio) {
        audio.volume = v;
        if (v > 0) audio.muted = false;
      }
    } else if (data.action === 'change_track' && typeof data.track_index === 'number') {
      userPausedRef.current = false;
      playTrackAt(data.track_index);
    }
  }, [storeId, playNext, playPrev, playTrackAt, reportKioskEvent]);

  // ── HTTP polling fallback for remote commands ───────────────────────────
  // WebSockets die silently through proxies and on backgrounded mobile tabs.
  // The kiosk drains queued commands every 3s over plain HTTP so Play /
  // Pause / Skip / Volume work even when the WS is dead. The dedup above
  // ensures WS + polling delivering the same command does not double-fire.
  useEffect(() => {
    if (!storeId) return;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const res = await authPost(`${API}/kiosk/${storeId}/drain-commands`, {});
        const cmds = Array.isArray(res.data?.commands) ? res.data.commands : [];
        for (const c of cmds) applyRemoteCommand(c);
      } catch (e) { /* transient network errors are fine, try again next tick */ }
    };
    // Poll immediately once (picks up anything queued before the kiosk loaded)
    // then every 3s. 3s is the sweet spot: fast enough to feel instant after
    // a WS outage, slow enough to keep backend load minimal (20 reqs/min per
    // kiosk).
    poll();
    const iv = setInterval(poll, 3000);
    return () => { stopped = true; clearInterval(iv); };
  }, [storeId, applyRemoteCommand]);

  // ── WebSocket — remote commands + schedule-changed pushes ───────────────
  useEffect(() => {
    if (!storeId) return;
    let ws;
    let closed = false;
    let pingTimer = null;
    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(`${WS_BASE}/api/ws/kiosk-${storeId}`);
        wsRef.current = ws;
        ws.onopen = () => {
          const track = tracksRef.current[trackIdxRef.current];
          sendStatus(true, !audioRef.current?.paused, track);
          // Keepalive: send a tiny ping every 25s so Cloudflare's 100s idle
          // timeout never drops this socket. Without this, the WS path goes
          // dormant after ~2 min of no activity and every remote command
          // has to wait for the HTTP fallback to catch it.
          if (pingTimer) clearInterval(pingTimer);
          pingTimer = setInterval(() => {
            try {
              if (ws && ws.readyState === 1 /* OPEN */) ws.send(JSON.stringify({ type: 'ping' }));
            } catch (e) { /* ignore */ }
          }, 25_000);
        };
        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.type === 'remote_command') {
              applyRemoteCommand(data);
            } else if (data.type === 'schedule_changed') {
              // Debounce — collapse a burst of schedule_changed pushes into one fetch.
              if (scheduleFetchRef._deb) clearTimeout(scheduleFetchRef._deb);
              scheduleFetchRef._deb = setTimeout(() => { fetchSchedule(); }, 250);
            }
          } catch (err) { /* ignore malformed */ }
        };
        ws.onclose = (e) => {
          if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
          // 4001 = another browser took over this kiosk session. Don't
          // reconnect; show the "session replaced" screen instead.
          if (e && e.code === 4001) {
            closed = true;
            setSessionReplaced(true);
            try { audioRef.current?.pause(); } catch (err) { /* ignore */ }
            return;
          }
          if (!closed) setTimeout(connect, 3000);
        };
        ws.onerror = () => { /* onclose will fire */ };
      } catch (err) { setTimeout(connect, 3000); }
    };
    connect();
    return () => {
      closed = true;
      if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
      if (ws) try { ws.close(); } catch (e) { /* ignore */ }
    };
  }, [storeId, sendStatus, applyRemoteCommand, fetchSchedule]);

  // ── Heartbeat every 5s ──────────────────────────────────────────────────
  useEffect(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(() => {
      const audio = audioRef.current;
      const track = tracksRef.current[trackIdxRef.current];
      authPost(`${API}/kiosk/${storeId}/update-status`, {
        is_online: true,
        is_playing: !!audio && !audio.paused,
        current_track: track ? { title: track.title, artist: track.artist } : null,
        current_time: audio?.currentTime || 0,
        duration: audio?.duration || 0,
        current_track_index: trackIdxRef.current,
        user_agent: deviceInfoRef.current.user_agent,
        geolocation: deviceInfoRef.current.geolocation,
      }).catch(() => {});
    }, HEARTBEAT_MS);
    return () => clearInterval(heartbeatRef.current);
  }, [storeId]);

  // ── Offline beacon on tab close / visibility change ─────────────────────
  useEffect(() => {
    const markOffline = () => {
      try {
        const blob = new Blob(
          [JSON.stringify({ is_online: false, is_playing: false, current_track: null })],
          { type: 'application/json' }
        );
        navigator.sendBeacon?.(`${API}/kiosk/${storeId}/update-status`, blob);
      } catch (e) { /* ignore */ }
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* ignore */ } }
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') markOffline();
      else {
        const t = tracksRef.current[trackIdxRef.current];
        sendStatus(true, !audioRef.current?.paused, t);
      }
    };
    window.addEventListener('beforeunload', markOffline);
    window.addEventListener('pagehide', markOffline);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('beforeunload', markOffline);
      window.removeEventListener('pagehide', markOffline);
      document.removeEventListener('visibilitychange', onVis);
      markOffline();
    };
  }, [storeId, sendStatus]);

  // ── User gesture handler (tap-to-start) ─────────────────────────────────
  const handleUnlock = useCallback(() => {
    userUnlockedRef.current = true;
    setNeedsUserGesture(false);

    // Prime the SECOND audio element with this same user gesture.
    // Safari/iOS requires EACH media element to be unlocked individually;
    // without this, the crossfade's next.play() is rejected forever, the
    // fade keeps aborting, and the player can loop the same song.
    const nextEl = nextAudioRef.current;
    if (nextEl && !nextEl.src) {
      try {
        nextEl.src = SILENT_WAV;
        nextEl.play().then(() => {
          nextEl.pause();
          nextEl.removeAttribute('src');
        }).catch(() => { /* priming is best-effort */ });
      } catch (e) { /* ignore */ }
    }

    // Piggy-back on the first user gesture to request precise geolocation.
    // We don't block audio on this — the prompt can be denied/ignored and
    // we'll still report user-agent. Captured value lands on the next
    // heartbeat / status update.
    if (typeof navigator !== 'undefined' && navigator.geolocation && !deviceInfoRef.current.geolocation) {
      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setDeviceInfo((prev) => ({
              ...prev,
              geolocation: {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
              },
            }));
          },
          () => { /* user denied or timed out — silently fall back to IP */ },
          { enableHighAccuracy: true, maximumAge: 60_000, timeout: 10_000 }
        );
      } catch (err) { /* unsupported */ }
    }

    const audio = audioRef.current;
    if (!audio) return;

    // If we already loaded a source but playback was blocked, just try again.
    if (audio.src && audio.paused) {
      audio.play().catch(() => {});
      return;
    }
    // Otherwise start the first track from the current schedule.
    if (tracksRef.current.length > 0) {
      playTrackAt(trackIdxRef.current || 0);
    }
  }, [playTrackAt]);

  // ── Render guards ───────────────────────────────────────────────────────
  const currentTrack = tracks[currentTrackIndex];

  if (sessionReplaced) {
    return (
      <div className="kiosk-fullscreen flex items-center justify-center" data-testid="kiosk-session-replaced">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 rounded-full border-2 border-amber-400/60 flex items-center justify-center mx-auto mb-6">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <p className="text-amber-300 font-mono text-2xl mb-3">Session closed</p>
          <p className="text-zinc-400 font-mono text-sm leading-relaxed">
            Another device opened this kiosk and took over playback. Only one active session is permitted per kiosk.
          </p>
          <p className="text-zinc-600 font-mono text-xs mt-6">
            Reload this page to reclaim the session.
          </p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="kiosk-fullscreen flex items-center justify-center" data-testid="kiosk-access-denied">
        <div className="text-center">
          <p className="text-red-500 font-mono text-2xl mb-4">Access Denied</p>
          <p className="text-zinc-500 font-mono">You can only access the kiosk for your assigned location.</p>
        </div>
      </div>
    );
  }
  if (error && tracks.length === 0) {
    return (
      <div className="kiosk-fullscreen flex items-center justify-center" data-testid="kiosk-error">
        <div className="text-center">
          <p className="text-red-500 font-mono text-2xl mb-4">{error}</p>
          <p className="text-zinc-600 font-mono text-sm mt-4">{isOnline ? 'Retrying...' : 'Waiting for connection'}</p>
        </div>
      </div>
    );
  }
  if (tracks.length === 0) {
    return (
      <div className="kiosk-fullscreen flex items-center justify-center" data-testid="kiosk-no-schedule">
        <div className="text-center">
          <p className="text-zinc-400 font-mono text-2xl mb-4">No schedule configured</p>
          <p className="text-zinc-600 font-mono text-sm">Configure dayparts or push a playlist from the dashboard</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kiosk-fullscreen" data-testid="kiosk-player">
      <div className="kiosk-background" style={{ background: branding.background_value }} />

      {/* Tap-to-start overlay — shown on every fresh load until the first user
          gesture. Every browser blocks autoplay without one; this makes the
          unlock explicit instead of leaving the page silently stuck. */}
      {needsUserGesture && (
        <button
          type="button"
          onClick={handleUnlock}
          onTouchStart={handleUnlock}
          onKeyDown={handleUnlock}
          className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/85 backdrop-blur-sm text-white"
          data-testid="kiosk-audio-unlock"
        >
          <div className="flex flex-col items-center gap-6 px-6 text-center">
            <div
              className="w-28 h-28 rounded-full border-2 flex items-center justify-center animate-pulse"
              style={{ borderColor: branding.accent_color + '88' }}
            >
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={branding.accent_color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold tracking-tight">Tap anywhere to start</p>
              <p className="text-sm font-mono text-white/70 max-w-md">
                Browsers require a tap before playing audio. This happens once per kiosk session.
              </p>
            </div>
          </div>
        </button>
      )}

      {/* Fatal error overlay — appears when 3 tracks in a row fail */}
      {fatalPlayError && !needsUserGesture && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 z-40 bg-red-500/90 text-white px-4 py-2 rounded-md font-mono text-sm shadow-lg"
          data-testid="kiosk-fatal-error"
        >
          {fatalPlayError}
        </div>
      )}

      {/* Status bar */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`} data-testid="kiosk-connection-indicator" />
          <span style={{ color: branding.secondary_text }} className="font-mono text-xs uppercase tracking-wider">
            {offlineMode ? 'Offline Mode' : isOnline ? 'Connected' : 'Reconnecting...'}
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full p-8">
        {branding.show_track_info && (
          <div className="text-center mb-8">
            <p style={{ color: branding.secondary_text }} className="font-mono text-sm uppercase tracking-[0.2em] mb-4">Now Playing</p>
            <h1 style={{ color: branding.text_color }} className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-4 leading-tight" data-testid="kiosk-track-title">
              {currentTrack?.title || 'Loading...'}
            </h1>
            <p style={{ color: branding.secondary_text }} className="text-2xl sm:text-3xl font-mono" data-testid="kiosk-track-artist">
              {currentTrack?.artist || ''}
            </p>
          </div>
        )}

        {isPlaying && branding.show_visualizer && (
          <div className="flex items-end gap-[3px] h-8 mb-6" data-testid="audio-visualizer">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="w-1.5 rounded-full animate-pulse" style={{ backgroundColor: branding.accent_color, height: `${10 + Math.random() * 22}px`, animationDelay: `${i * 0.12}s`, animationDuration: '0.8s' }} />
            ))}
          </div>
        )}

        <div className="w-full max-w-xl px-4" data-testid="kiosk-progress-bar">
          <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden mb-2">
            <div className="h-full rounded-full transition-all duration-1000 ease-linear" style={{ width: `${progress}%`, backgroundColor: branding.accent_color }} />
          </div>
          <div className="flex justify-between">
            <span style={{ color: branding.secondary_text, opacity: 0.6 }} className="font-mono text-xs">{formatTime(currentTime)}</span>
            <span style={{ color: branding.secondary_text, opacity: 0.6 }} className="font-mono text-xs">{formatTime(duration)}</span>
          </div>
        </div>
      </div>

      {/* Bottom info */}
      <div className="absolute bottom-6 left-6 right-6 z-20 flex items-end justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: isPlaying ? branding.accent_color : '#52525b' }} />
            <p style={{ color: branding.secondary_text }} className="font-mono text-sm uppercase tracking-wider" data-testid="kiosk-state-badge">
              {isPlaying ? 'Playing' : 'Paused'}
            </p>
          </div>
          {offlineMode && <p className="text-yellow-600 font-mono text-xs mt-1" data-testid="kiosk-offline-badge">Playing from local cache</p>}
          <p style={{ color: branding.secondary_text, opacity: 0.4 }} className="font-mono text-xs mt-1" data-testid="kiosk-volume">Vol: {Math.round(volume * 100)}%</p>
        </div>
        <div className="text-right">
          <p style={{ color: branding.secondary_text }} className="font-mono text-sm">
            {scheduleType === 'special_occasion' ? 'Special Occasion' : scheduleType === 'daypart' ? 'Daypart' : (playlistName || 'Schedule')}
          </p>
          <p style={{ color: branding.secondary_text, opacity: 0.4 }} className="font-mono text-xs mt-1">
            Track {currentTrackIndex + 1} / {tracks.length}
          </p>
          <p style={{ color: branding.secondary_text, opacity: 0.3 }} className="font-mono text-[10px] mt-2 max-w-[240px] text-right leading-tight" data-testid="kiosk-license-disclaimer">
            Playback of legally-licensed content only. Subscriber is responsible for all public-performance licenses.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Kiosk;
